/**
 * Gestión → Administración → Canjes masivos de notas de venta
 * (`/gestion/administracion/canjes-notas`).
 *
 * Puerto de `Administration::exchange_sales_notes` + `views/menu/administrador/canjes_notas.php`
 * (liquidar) y de `Administration::historial_canje_masivo` +
 * `views/menu/dashboard/exchangue_masive.php` (el histórico con su anulación).
 *
 * Qué se porta:
 * - El **listado de notas de venta** del rango (con filtro de producto y de
 *   estado: por liquidar, liquidadas o todas) con su cliente, placa, producto,
 *   cantidad e importe.
 * - La **liquidación**: las notas seleccionadas se agrupan en **una venta
 *   administrativa** con su comprobante (`FACTURA` o `BOLETA`, serie
 *   administrativa, condición de pago con su plazo si es a crédito, referencia e
 *   información adicional) que queda en la cola de SUNAT.
 * - Las **líneas** de la venta, que el original armaba con los productos
 *   seleccionados y sus placas.
 * - El **histórico** de canjes (`canjes_notas_ventas`) con su ejecutor y su
 *   **anulación**, que devuelve las notas a «por liquidar».
 */

import { BadgeX, HandCoins, Info, Lock, Package, RefreshCw, Search, ShoppingCart } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { AdministracionNav } from '../components/administracion-nav';
import {
  useAnularCanje,
  useCatalogosCanje,
  useHistorialCanjes,
  useLiquidarNotas,
  useNotas,
} from '../administracion-canjes.api';
import type { FiltrosNotas, LineaCanje, NotaVenta } from '../administracion-canjes.types';
import { fechaCorta, hoyIso, hoyMenosDias, monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const PLAZOS = [7, 15, 20, 30, 40, 45];

/** Primer día de hace dos meses (como el rango por defecto del original). */
function inicioPorDefecto(): string {
  const hoy = new Date();
  hoy.setMonth(hoy.getMonth() - 2);

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

export function CanjesNotasPage() {
  useDocumentTitle('Gestión · Canjes masivos de notas');

  const [desde, setDesde] = useState(inicioPorDefecto);
  const [hasta, setHasta] = useState(() => hoyIso());
  const [producto, setProducto] = useState('');
  const [estado, setEstado] = useState<FiltrosNotas['estado']>('por_liquidar');
  const [busqueda, setBusqueda] = useState('');
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [liquidar, setLiquidar] = useState(false);
  const [canjeAnular, setCanjeAnular] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosNotas>(
    () => ({
      desde,
      hasta,
      producto_id: producto || undefined,
      estado,
    }),
    [desde, hasta, producto, estado],
  );

  const consulta = useNotas(filtros);
  const historial = useHistorialCanjes();
  const anular = useAnularCanje();

  const notas = consulta.data?.notas ?? [];
  const visibles = notas.filter((nota) => {
    if (!busqueda.trim()) return true;

    const texto = busqueda.trim().toLowerCase();

    return [nota.nota, nota.cliente, nota.placa, nota.producto]
      .filter(Boolean)
      .some((valor) => String(valor).toLowerCase().includes(texto));
  });

  // Paginación en el navegador: el resumen y la selección siguen calculándose
  // sobre todas las notas del filtro; aquí sólo se decide qué filas se pintan.
  const clavePaginacion = useMemo(() => [filtros, busqueda] as const, [filtros, busqueda]);
  const paginacion = usePaginacion(visibles, { clave: clavePaginacion });

  const seleccionadas = notas.filter((nota) => seleccion.has(nota.id));
  const total = seleccionadas.reduce((suma, nota) => suma + nota.total, 0);

  /** Marca o desmarca una nota. */
  const alternar = (id: number) => {
    setSeleccion((previo) => {
      const copia = new Set(previo);

      if (copia.has(id)) copia.delete(id);
      else copia.add(id);

      return copia;
    });
  };

  /** Abre la liquidación con lo seleccionado. */
  const abrirLiquidacion = () => {
    setError(null);
    setLiquidar(true);
  };

  const cerrar = () => {
    setLiquidar(false);
    setSeleccion(new Set());
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Canjes masivos de notas de venta</h1>
        <p className="mt-1 text-sm text-ink-500">
          Liquida las notas de venta seleccionadas en una sola venta administrativa con su
          comprobante para SUNAT.
        </p>
      </header>

      <AdministracionNav active="canjes" />

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Input
          label="Desde"
          type="date"
          value={desde}
          max={hoyIso()}
          onChange={(evento) => setDesde(evento.target.value)}
        />
        <Input
          label="Hasta"
          type="date"
          value={hasta}
          max={hoyIso()}
          onChange={(evento) => setHasta(evento.target.value)}
        />
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Producto</span>
          <Select
            className={CONTROL}
            value={producto}
            onChange={(evento) => setProducto(evento.target.value)}
          >
            <option value="">Todos</option>
            {['1', '2', '3', '4', '5'].map((id) => (
              <option key={id} value={id}>
                Producto #{id}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Estado</span>
          <Select
            className={CONTROL}
            value={estado}
            onChange={(evento) => setEstado(evento.target.value as FiltrosNotas['estado'])}
          >
            <option value="por_liquidar">Por liquidar</option>
            <option value="liquidadas">Liquidadas</option>
            <option value="todas">Todas</option>
          </Select>
        </label>
        <Input
          label="Buscar"
          placeholder="Nota, cliente, placa o producto"
          leftIcon={<Search className="size-4" aria-hidden="true" />}
          value={busqueda}
          onChange={(evento) => setBusqueda(evento.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-500">
          {consulta.data
            ? `${visibles.length} nota(s) · ${consulta.data.resumen.cantidad.toFixed(3)} GLL · ${
                monto(consulta.data.resumen.importe)
              }`
            : 'Cargando…'}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!paginacion.visibles.length}
            onClick={() => setSeleccion(new Set(paginacion.visibles.map((nota) => nota.id)))}
          >
            Seleccionar página
          </Button>
          <Button variant="ghost" size="sm" disabled={!seleccion.size} onClick={() => setSeleccion(new Set())}>
            Limpiar
          </Button>
          <Button
            size="sm"
            disabled={!seleccion.size}
            leftIcon={<HandCoins className="size-4" aria-hidden="true" />}
            onClick={abrirLiquidacion}
          >
            Liquidar ({seleccion.size}) · {monto(total)}
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => {
              void consulta.refetch();
              void historial.refetch();
            }}
          >
            Actualizar
          </Button>
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron leer las notas">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {consulta.data ? (
        <div className="overflow-hidden rounded-card border border-ink-100">
          <div className="overflow-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
              <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-500"
                    aria-label="Seleccionar todas las notas de la página"
                    checked={
                      paginacion.visibles.length > 0 &&
                      paginacion.visibles.every((nota) => seleccion.has(nota.id))
                    }
                    onChange={(evento) => {
                      if (evento.target.checked)
                        setSeleccion(new Set(paginacion.visibles.map((nota) => nota.id)));
                      else setSeleccion(new Set());
                    }}
                  />
                </th>
                <th className="px-3 py-2 whitespace-nowrap">Nota</th>
                <th className="px-3 py-2 whitespace-nowrap">Fecha</th>
                <th className="px-3 py-2 whitespace-nowrap">Cliente</th>
                <th className="px-3 py-2 whitespace-nowrap">Placa</th>
                <th className="px-3 py-2 whitespace-nowrap">Producto</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">P. unitario</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                <th className="px-3 py-2 whitespace-nowrap">Liquidada</th>
              </tr>
            </thead>
            <tbody>
              {paginacion.visibles.map((nota: NotaVenta) => (
                <tr
                  key={nota.id}
                  className={cn('border-t border-ink-100', seleccion.has(nota.id) && 'bg-brand-50/40')}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand-500"
                      aria-label={`Seleccionar la nota ${nota.nota}`}
                      checked={seleccion.has(nota.id)}
                      onChange={() => alternar(nota.id)}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{nota.nota}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(nota.fecha)}</td>
                  <td className="max-w-[220px] truncate px-3 py-2" title={nota.cliente ?? ''}>
                    {nota.cliente ?? '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{nota.placa ?? '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{nota.producto ?? '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{nota.cantidad.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{monto(nota.precio)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{monto(nota.total)}</td>
                  <td className="px-3 py-2">
                    {nota.liquidada ? (
                      <Badge variant="success">{nota.cpe_generate ?? 'Sí'}</Badge>
                    ) : (
                      <Badge variant="warning">Por liquidar</Badge>
                    )}
                  </td>
                </tr>
              ))}

              {visibles.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-ink-500" colSpan={10}>
                    No hay notas de venta que cumplan los filtros.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>

          <Paginador {...paginacion} etiqueta="notas" />
        </div>
      ) : null}

      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <Package className="size-4 text-brand-500" aria-hidden="true" />
          Histórico de canjes ({historial.data?.total ?? 0})
        </h2>

        {historial.isLoading ? <Skeleton className="h-40 w-full" /> : null}

        {historial.data && historial.data.canjes.length === 0 ? (
          <p className="text-sm text-ink-500">Todavía no hay canjes masivos registrados.</p>
        ) : null}

        {historial.data && historial.data.canjes.length > 0 ? (
          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-3 py-2">Canje</th>
                  <th className="px-3 py-2">Emisión</th>
                  <th className="px-3 py-2 text-right">Notas</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2">Ejecutor</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {historial.data.canjes.map((canje) => (
                  <tr key={canje.id} className="border-t border-ink-100">
                    <td className="px-3 py-2 whitespace-nowrap">#{canje.id}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(canje.fecha_emision)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{canje.cantidad}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(canje.monto)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{canje.ejecutor || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant={canje.estado === 'ANULADO' ? 'danger' : 'success'}>
                        {canje.estado ?? '—'}
                      </Badge>
                      {canje.estado === 'ANULADO' ? (
                        <span className="mt-1 block text-[11px] text-ink-500">
                          {canje.anulado_por || ''} · {fechaCorta(canje.anulado_fecha)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={canje.estado === 'ANULADO' || !canje.notas.length}
                        leftIcon={<BadgeX className="size-4 text-rose-600" aria-hidden="true" />}
                        onClick={() => setCanjeAnular(canje.id)}
                      >
                        Anular
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <Alert variant="info" title="Pendiente con candado">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              La «línea de tiempo» de la vista del original (los filtros se aplican a los `selects`
              del propio DataTables), que aquí se resuelven en el servidor.
            </span>
          </li>
        </ul>
      </Alert>

      <LiquidarDialog
        key={`liquidar-${seleccionadas.map((nota) => nota.id).join('-')}`}
        open={liquidar}
        notas={seleccionadas}
        onClose={cerrar}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          cerrar();
        }}
      />

      <ConfirmDialog
        open={canjeAnular !== null}
        title="Anular el canje"
        description={`Se marcará el canje #${canjeAnular ?? ''} como anulado y sus notas volverán a estar por liquidar.`}
        confirmLabel="Anular"
        tone="danger"
        loading={anular.isPending}
        onClose={() => setCanjeAnular(null)}
        onConfirm={() => {
          if (canjeAnular === null) return;

          anular.mutate(canjeAnular, {
            onSuccess: (respuesta) => {
              setCanjeAnular(null);
              setAviso(respuesta.mensaje);
            },
            onError: (fallo) => {
              setCanjeAnular(null);
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo anular el canje.');
            },
          });
        }}
      >
        <Alert variant="warning" title="Comprueba antes de anular">
          <span className={ICONO_EN_ALERTA}>
            <Info className="size-4" aria-hidden="true" />
            La anulación devuelve las notas a «por liquidar», pero no toca el comprobante que ya se
            haya emitido: para eso está la anulación de comprobantes.
          </span>
        </Alert>
      </ConfirmDialog>
    </div>
  );
}

/** Diálogo de liquidación: arma la venta administrativa con las notas elegidas. */
function LiquidarDialog({
  open,
  notas,
  onClose,
  onAviso,
}: {
  open: boolean;
  notas: NotaVenta[];
  onClose: () => void;
  onAviso: (mensaje: string) => void;
}) {
  const [tipo, setTipo] = useState<'FACTURA' | 'BOLETA'>('FACTURA');
  const [clienteId, setClienteId] = useState(0);
  const [serie, setSerie] = useState('');
  const [fecha, setFecha] = useState(() => hoyIso());
  const [metodo, setMetodo] = useState<'CONTADO' | 'CREDITO'>('CONTADO');
  const [plazo, setPlazo] = useState(7);
  const [referencia, setReferencia] = useState('');
  const [informacion, setInformacion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const catalogo = useCatalogosCanje(tipo);
  const liquidar = useLiquidarNotas();

  const [firma, setFirma] = useState('');

  // Al llegar el catálogo se preselecciona la primera serie.
  if (catalogo.data && firma !== `${tipo}-${catalogo.data.series.join(',')}`) {
    setFirma(`${tipo}-${catalogo.data.series.join(',')}`);
    setSerie(catalogo.data.series[0] ?? '');
  }

  /** Agrupa las notas por producto para armar las líneas de la venta. */
  const lineas: LineaCanje[] = useMemo(() => {
    const agrupado = new Map<number, LineaCanje>();

    for (const nota of notas) {
      const actual = agrupado.get(nota.producto_id) ?? {
        producto_id: nota.producto_id,
        producto: nota.producto ?? `Producto ${nota.producto_id}`,
        cantidad: 0,
        precio: nota.precio,
        total: 0,
        placas: [],
      };

      actual.cantidad = Math.round((actual.cantidad + nota.cantidad) * 1000) / 1000;
      actual.total = Math.round((actual.total + nota.total) * 100) / 100;

      if (nota.placa && !actual.placas.includes(nota.placa)) actual.placas.push(nota.placa);

      agrupado.set(nota.producto_id, actual);
    }

    return [...agrupado.values()];
  }, [notas]);

  const total = Math.round(lineas.reduce((suma, linea) => suma + linea.total, 0) * 100) / 100;

  const enviar = () => {
    setError(null);
    liquidar.mutate(
      {
        cliente_id: clienteId,
        tipo_documento: tipo,
        serie,
        fecha_emision: fecha,
        metodo_pago: metodo,
        dias_credito: metodo === 'CREDITO' ? plazo : undefined,
        referencia: referencia.trim() || undefined,
        informacion_adicional: informacion.trim() || undefined,
        total,
        lineas,
        notas: notas.map((nota) => nota.id),
      },
      {
        onSuccess: (respuesta) => onAviso(respuesta.mensaje),
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo liquidar las notas.'),
      },
    );
  };

  const cliente = catalogo.data?.clientes.find((item) => item.id === clienteId);
  const puedeEnviar = Boolean(clienteId && serie && notas.length) && !liquidar.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title="Liquidar notas de venta"
      description={`${notas.length} nota(s) por ${monto(total)}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!puedeEnviar}
            loading={liquidar.isPending}
            leftIcon={<ShoppingCart className="size-4" aria-hidden="true" />}
            onClick={enviar}
          >
            Generar la venta
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de comprobante</span>
            <Select
              className={CONTROL}
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as 'FACTURA' | 'BOLETA')}
            >
              <option value="FACTURA">FACTURA</option>
              <option value="BOLETA">BOLETA</option>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm lg:col-span-2">
            <span className="font-medium text-ink-700">Cliente</span>
            <Select
              className={CONTROL}
              value={clienteId}
              onChange={(evento) => setClienteId(Number(evento.target.value))}
              searchable
            >
              <option value={0}>SELECCIONE</option>
              {(catalogo.data?.clientes ?? []).map((item) => (
                <option key={item.id} value={item.id} data-search={item.numero_doc}>
                  {item.nombre} · {item.numero_doc}
                </option>
              ))}
            </Select>
            <span className="text-xs text-ink-500">
              {cliente
                ? `${cliente.tipo_doc === '6' ? 'RUC' : 'DNI'} ${cliente.numero_doc} · retención ${cliente.retencion}%`
                : 'La FACTURA sólo se puede emitir a un cliente con RUC.'}
            </span>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Serie</span>
            <Select className={CONTROL} value={serie} onChange={(evento) => setSerie(evento.target.value)}>
              {(catalogo.data?.series ?? []).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Select>
            {catalogo.data && catalogo.data.series.length === 0 ? (
              <span className="text-xs text-rose-600">Sin series administrativas para {tipo}.</span>
            ) : null}
          </label>

          <Input
            label="Fecha de emisión"
            type="date"
            min={hoyMenosDias(catalogo.data?.dias_boleta_masiva ?? 0)}
            max={hoyIso()}
            value={fecha}
            onChange={(evento) => setFecha(evento.target.value)}
          />

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Método de pago</span>
            <Select
              className={CONTROL}
              value={metodo}
              onChange={(evento) => setMetodo(evento.target.value as 'CONTADO' | 'CREDITO')}
            >
              <option value="CONTADO">CONTADO</option>
              <option value="CREDITO">CRÉDITO</option>
            </Select>
          </label>

          {metodo === 'CREDITO' ? (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Plazo (días)</span>
              <Select
                className={CONTROL}
                value={plazo}
                onChange={(evento) => setPlazo(Number(evento.target.value))}
              >
                {PLAZOS.map((dias) => (
                  <option key={dias} value={dias}>
                    {dias} días
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <Input
              label="Referencia (opcional)"
              value={referencia}
              onChange={(evento) => setReferencia(evento.target.value)}
            />
          )}

          <Input
            label="Información adicional (opcional)"
            containerClassName="lg:col-span-2"
            value={informacion}
            onChange={(evento) => setInformacion(evento.target.value)}
          />
        </div>

        {error ? (
          <Alert variant="error" title="No se pudo liquidar">
            {error}
          </Alert>
        ) : null}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink-800">
            Líneas de la venta ({lineas.length}) · {monto(total)}
          </h3>
          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-3 py-2">Producto</th>
                  <th className="px-3 py-2 text-right">Cantidad</th>
                  <th className="px-3 py-2 text-right">P. unitario</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">Placas</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((linea) => (
                  <tr key={linea.producto_id} className="border-t border-ink-100">
                    <td className="px-3 py-2">{linea.producto}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{linea.cantidad.toFixed(3)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(linea.precio)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(linea.total)}</td>
                    <td className="px-3 py-2 text-xs text-ink-500">
                      {linea.placas.length ? linea.placas.join(', ') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-ink-50/60">
                <tr className="border-t border-ink-100 font-semibold text-ink-800">
                  <td className="px-3 py-2" colSpan={3}>
                    Total de la venta
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{monto(total)}</td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <Alert variant="warning" title="Qué se genera">
          <span className={ICONO_EN_ALERTA}>
            <Info className="size-4" aria-hidden="true" />
            Una sola venta administrativa con su comprobante en la cola de SUNAT (`En Validación`),
            con las placas de cada producto en el detalle. Las notas quedan marcadas como
            liquidadas con el código `ADM-&lt;venta&gt;`, que es lo que las puede anular después.
          </span>
        </Alert>
      </div>
    </Dialog>
  );
}
